import React, { useState, useRef, useCallback } from 'react';
import FeatureLockScreen from '../../components/apple-ui/FeatureLockScreen';
import ReferralToast from '../../components/apple-ui/ReferralToast';
import { shouldShowExportReferral, markExportReferralShown, shouldShowFirstFeatureReferral, markFirstFeatureUsed, EXPORT_MSG, FIRST_FEATURE_MSG } from '../../utils/referralTriggers';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { MainLayoutApple, CardApple, ButtonApple, BadgeApple, GlobalContextBarApple } from '@/components/apple-ui';
import CostEstimateApple from '@/components/apple-ui/CostEstimateApple';
import ContextSelectorModal from '@/components/ContextSelectorModal';
import { useTheme } from '@/contexts/ThemeContext';
import { useGlobalContext } from '@/contexts/GlobalContextProvider';
import { usePlan } from '@/contexts/PlanContext';
import { 
  Upload, FileText, BarChart3, Download, PlayCircle, Target, X, CheckCircle, AlertCircle,
  Loader2, File, Info, Settings, ChevronDown, ChevronUp, Package
} from 'lucide-react';
import { toast } from 'sonner';
import ExportFeedbackModal from '@/components/ExportFeedbackModal';
import TranslationProgress from '../../components/TranslationProgress';
import '@/styles/apple-design-system.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'en', name: 'English' }, { code: 'hi', name: 'Hindi' }, { code: 'gu', name: 'Gujarati' },
  { code: 'ta', name: 'Tamil' }, { code: 'te', name: 'Telugu' }, { code: 'kn', name: 'Kannada' },
  { code: 'ml', name: 'Malayalam' }, { code: 'bn', name: 'Bengali' }, { code: 'mr', name: 'Marathi' }, { code: 'pa', name: 'Punjabi' },
];

const STYLE_OPTIONS = [
  { value: 'general', label: 'General', desc: 'Balanced tone' },
  { value: 'formal', label: 'Formal', desc: 'Professional' },
  { value: 'casual', label: 'Casual', desc: 'Friendly tone' },
  { value: 'technical', label: 'Technical', desc: 'Preserve terms' },
  { value: 'creative', label: 'Creative', desc: 'Expressive' },
  { value: 'legal', label: 'Legal', desc: 'Legal terminology' },
  { value: 'medical', label: 'Medical', desc: 'Healthcare' },
];

const MTPEProPageApple = ({ user, planInfo, onLogout, onPlanUpdate }) => {
  const [activeTab, setActiveTab] = useState('batch');
  const { isDarkMode } = useTheme();
  const { showContextModal, setShowContextModal, getContextIds } = useGlobalContext();
  const { updateFromApiResponse } = usePlan();
  const mtpeAdvancedAccess = useFeatureAccess('mtpe_advanced');

  // Feature gating
  if (!mtpeAdvancedAccess.hasAccess) {
    return <FeatureLockScreen feature="mtpe" requiredPlan={mtpeAdvancedAccess.upgradeTo || 'starter'} currentPlan={mtpeAdvancedAccess.currentPlan} />;
  }

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      {/* Context Selector Modal */}
      <ContextSelectorModal 
        isOpen={showContextModal} 
        onClose={() => setShowContextModal(false)} 
        variant="apple"
      />
      
      <div className="apple-content" data-testid="apple-mtpe-pro-page">
        {/* Page Header */}
        <div className="flex items-start gap-4 mb-8">
          <div className="relative">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Target size={32} className="text-white" />
            </div>
            <span className="absolute -top-1 -right-1 px-2 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full">PRO</span>
          </div>
          <div>
            <h1 className="apple-content-title">MTPE Pro</h1>
            <p className="apple-content-subtitle">Advanced machine translation post-editing with batch processing and quality analysis</p>
          </div>
        </div>

        {/* Global Context Bar */}
        <GlobalContextBarApple 
          onOpenModal={() => setShowContextModal(true)} 
          isDarkMode={isDarkMode}
        />

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-gray-200 mb-8">
          <button className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-t-xl border-b-2 transition-all ${activeTab === 'batch' ? 'border-purple-600 text-purple-600 bg-purple-50' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('batch')} data-testid="tab-batch">
            <Upload size={18} />Batch Processing
          </button>
          <button className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-t-xl border-b-2 transition-all ${activeTab === 'quality' ? 'border-purple-600 text-purple-600 bg-purple-50' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('quality')} data-testid="tab-quality">
            <BarChart3 size={18} />Quality Analysis
          </button>
        </div>

        {activeTab === 'batch' && <BatchProcessingPanel />}
        {activeTab === 'quality' && <QualityAnalysisPanel />}
      </div>
    </MainLayoutApple>
  );
};

const BatchProcessingPanel = () => {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [targetLang, setTargetLang] = useState('Hindi');
  const [selectedStyle, setSelectedStyle] = useState('general');
  const [isProcessing, setIsProcessing] = useState(false);
  const [procStep, setProcStep] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [applyGlossary, setApplyGlossary] = useState(true);
  const [checkMemory, setCheckMemory] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [instructions, setInstructions] = useState({ fixGrammar: true, improveNaturalness: true, maintainMeaning: true, adaptIdioms: false, formalTone: false, keepTechnical: true });
  const [customInstructions, setCustomInstructions] = useState('');
  const totalWordCount = files.reduce((sum, f) => sum + (f.estimatedWords || 0), 0);
  const [showExportFeedback, setShowExportFeedback] = useState(false);
  const [referralToast, setReferralToast] = useState(null);
  const { getContextIds } = useGlobalContext();
  const { updateFromApiResponse } = usePlan();

  const triggerExportReferral = () => {
    if (shouldShowExportReferral()) {
      markExportReferralShown();
      setReferralToast({ message: EXPORT_MSG, buttons: 'dismiss' });
    }
  };
  const triggerFirstFeatureReferral = () => {
    if (shouldShowFirstFeatureReferral()) {
      markFirstFeatureUsed();
      setTimeout(() => setReferralToast(prev => prev || { message: FIRST_FEATURE_MSG, buttons: 'remind_later' }), 3000);
    }
  };

  const buildInstructionsString = () => {
    let text = '';
    if (instructions.fixGrammar) text += '- Fix grammar and spelling\n';
    if (instructions.improveNaturalness) text += '- Improve naturalness\n';
    if (instructions.maintainMeaning) text += '- Maintain exact meaning\n';
    if (instructions.adaptIdioms) text += '- Adapt idioms\n';
    if (instructions.formalTone) text += '- Use formal tone\n';
    if (instructions.keepTechnical) text += '- Keep technical terms\n';
    if (customInstructions) text += `\nAdditional: ${customInstructions}`;
    return text;
  };

  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleDrop = useCallback((e) => { e.preventDefault(); setIsDragging(false); addFiles(Array.from(e.dataTransfer.files)); }, [targetLang, selectedStyle]);
  const handleFileSelect = (e) => { addFiles(Array.from(e.target.files || [])); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const extractWordCount = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    try {
      if (ext === 'txt' || ext === 'csv') {
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => { const t = e.target.result || ''; resolve(t.trim().split(/\s+/).filter(w => w.length > 0).length); };
          reader.onerror = () => resolve(Math.round(file.size / 10));
          reader.readAsText(file);
        });
      } else if (ext === 'docx') {
        const mammoth = (await import('mammoth')).default;
        const ab = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer: ab });
        return result.value.trim().split(/\s+/).filter(w => w.length > 0).length;
      } else if (ext === 'xlsx') {
        const JSZip = (await import('jszip')).default;
        const ab = await file.arrayBuffer();
        const zip = await JSZip.loadAsync(ab);
        let allText = '';
        const sf = zip.file('xl/sharedStrings.xml');
        if (sf) { const xml = await sf.async('text'); const doc = new DOMParser().parseFromString(xml, 'application/xml'); doc.querySelectorAll('si t').forEach(n => { allText += ' ' + n.textContent; }); }
        const ws = zip.file('xl/worksheets/sheet1.xml');
        if (ws) { const xml = await ws.async('text'); const doc = new DOMParser().parseFromString(xml, 'application/xml'); doc.querySelectorAll('is t').forEach(n => { allText += ' ' + n.textContent; }); }
        return allText.trim().split(/\s+/).filter(w => w.length > 0).length;
      }
    } catch (e) { console.error('Word count extraction failed:', e); }
    return Math.round(file.size / (ext === 'pdf' ? 8 : 10));
  };

  const addFiles = async (newFiles) => {
    const validExtensions = ['docx', 'xlsx', 'txt', 'pdf'];
    const validFiles = [], skipped = [];
    newFiles.forEach(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (validExtensions.includes(ext)) {
        validFiles.push({
          id: Math.random().toString(36).substr(2, 9),
          file,
          targetLang,
          style: selectedStyle,
          status: 'pending',
          progress: 0,
          mtOutput: null,
          translatedText: null,
          inputFormat: ext,
          estimatedWords: 0,
          wordCountSource: 'pending'
        });
      } else { skipped.push(file.name); }
    });
    if (skipped.length > 0) toast.warning(`${skipped.length} file(s) skipped. Supported: DOCX, XLSX, TXT, PDF`);
    if (validFiles.length === 0) return;
    setFiles(prev => [...prev, ...validFiles]);
    toast.success(`${validFiles.length} file(s) added`);
    for (const entry of validFiles) {
      const count = await extractWordCount(entry.file);
      const src = entry.file.name.split('.').pop().toLowerCase() === 'pdf' ? 'estimated' : 'extracted';
      setFiles(prev => prev.map(f => f.id === entry.id ? { ...f, estimatedWords: count, wordCountSource: src } : f));
    }
  };

  const removeFile = (id) => { setFiles(prev => prev.filter(f => f.id !== id)); };

  const processBatch = async () => {
    if (files.length === 0) { toast.error('Please add files'); return; }
    setIsProcessing(true);
    setProcStep(1);
    const token = localStorage.getItem('token');

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setFiles(prev => prev.map(f => f.id === file.id ? { ...f, status: 'processing', progress: 0 } : f));
      setProcStep(2);

      try {
        const formData = new FormData();
        const contextIds = getContextIds();
        formData.append('file', file.file);
        setProcStep(3);
        formData.append('target_lang', file.targetLang);
        formData.append('style', file.style || 'general');
        formData.append('apply_glossary', String(applyGlossary));
        formData.append('check_memory', String(checkMemory));
        formData.append('instructions', buildInstructionsString());
        if (contextIds.instructionId) formData.append('instruction_id', contextIds.instructionId);
        if (contextIds.glossaryId || contextIds.projectId) formData.append('glossary_id', contextIds.glossaryId || contextIds.projectId);
        
        const response = await fetch(`${API_URL}/api/mtpe/batch-process`, { 
          method: 'POST', 
          headers: { Authorization: `Bearer ${token}` }, 
          body: formData 
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Processing failed');
        }
        
        const data = await response.json();
        
        // Update navbar balance after batch processing
        updateFromApiResponse(data);
        
        setFiles(prev => prev.map(f => f.id === file.id ? {
          ...f,
          status: 'completed',
          progress: 100,
          mtOutput: data.output_url,
          translatedText: data.translated_text,
          originalWordCount: data.original_word_count,
          estimatedWords: data.original_word_count,
          wordCountSource: 'actual',
          creditsUsed: data.credits_used,
          jobId: data.job_id,
          outputFilename: data.output_filename
        } : f));
        
        toast.success(`${file.file.name} processed (${data.original_word_count} words)`);
      } catch (error) {
        console.error('MTPE batch error:', error);
        setFiles(prev => prev.map(f => f.id === file.id ? { ...f, status: 'error', progress: 0, errorMessage: error.message } : f));
        toast.error(`Failed: ${file.file.name} - ${error.message}`);
      }
    }
    
    setIsProcessing(false);
    setProcStep(4);
    setTimeout(() => setProcStep(0), 2000);
    const completedCount = files.filter(f => f.status === 'completed').length + 1;
    if (completedCount > 0) {
      toast.success(`Batch processing completed! ${completedCount} file(s) ready for download.`);
      triggerFirstFeatureReferral();
    }
  };

  // Download translated file - creates document in same format as input
  const downloadFile = async (file) => {
    if (!file.translatedText) {
      if (file.mtOutput) {
        window.open(file.mtOutput, '_blank');
        return;
      }
      toast.error('No translated content available');
      return;
    }
    
    const inputFormat = file.inputFormat || 'txt';
    const baseFileName = file.file.name.replace(/\.[^.]+$/, '');
    
    try {
      if (inputFormat === 'txt') {
        // Plain text download
        const blob = new Blob([file.translatedText], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseFileName}_translated.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Downloaded as TXT');
        setShowExportFeedback(true);
        triggerExportReferral();
      } else if (inputFormat === 'docx') {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('translated_text', file.translatedText);
        formData.append('filename', baseFileName + '_translated');
        const response = await fetch(`${API_URL}/api/mtpe/export-docx`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${baseFileName}_translated.docx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          toast.success('Downloaded as DOCX');
          setShowExportFeedback(true);
          triggerExportReferral();
        } else {
          const blob = new Blob([file.translatedText], { type: 'text/plain;charset=utf-8' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${baseFileName}_translated.txt`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          toast.info('Downloaded as TXT (DOCX export unavailable)');
        }
      } else if (inputFormat === 'xlsx') {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('translated_text', file.translatedText);
        formData.append('filename', baseFileName + '_translated');
        const response = await fetch(`${API_URL}/api/mtpe/export-xlsx`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${baseFileName}_translated.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          toast.success('Downloaded as XLSX');
          setShowExportFeedback(true);
          triggerExportReferral();
        } else {
          const blob = new Blob([file.translatedText], { type: 'text/plain;charset=utf-8' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${baseFileName}_translated.txt`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          toast.info('Downloaded as TXT (XLSX export unavailable)');
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed');
    }
  };

  const downloadAll = async () => { 
    const completedFiles = files.filter(f => f.status === 'completed');
    if (completedFiles.length === 0) {
      toast.error('No completed files to download');
      return;
    }
    
    // If any files have jobId, use backend ZIP download
    const jobIds = completedFiles.filter(f => f.jobId).map(f => f.jobId);
    if (jobIds.length > 0) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/mtpe/download-zip`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify(jobIds)
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'mtpe_translations.zip';
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          toast.success('Downloaded ZIP archive');
          setShowExportFeedback(true);
          return;
        }
      } catch (error) {
        console.error('ZIP download error:', error);
      }
    }
    
    // Fallback: download individually
    completedFiles.forEach(file => downloadFile(file)); 
  };
  const getFileIcon = (filename) => { const ext = filename.split('.').pop().toLowerCase(); const colors = { pdf: 'text-red-500', docx: 'text-blue-500', xlsx: 'text-green-500', txt: 'text-gray-500' }; return <File className={`h-5 w-5 ${colors[ext] || 'text-gray-500'}`} />; };

  return (
    <div data-testid="batch-processing-panel">
      {procStep > 0 && (
        <TranslationProgress step={procStep} steps={['Uploading Document', 'Extracting Segments', 'Post-Editing Content', 'Ready to Download']} />
      )}
      {/* Settings */}
      <CardApple className="mb-6">
        <CardApple.Body>
          <div className="grid grid-cols-2 gap-6 mb-4">
            <div><label className="text-xs font-medium text-gray-500 mb-1 block">Target Language</label><select value={targetLang} onChange={(e) => setTargetLang(e.target.value)} className="apple-input apple-select" data-testid="target-lang-select">{LANGUAGES.map(lang => <option key={lang.code} value={lang.name}>{lang.name}</option>)}</select></div>
            <div><label className="text-xs font-medium text-gray-500 mb-1 block">Document Style</label><select value={selectedStyle} onChange={(e) => setSelectedStyle(e.target.value)} className="apple-input apple-select" data-testid="style-select">{STYLE_OPTIONS.map(style => <option key={style.value} value={style.value}>{style.label} - {style.desc}</option>)}</select></div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="mtpe-pro-glossary-toggle"><input type="checkbox" checked={applyGlossary} onChange={(e) => setApplyGlossary(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-purple-600" /><span className="text-sm text-gray-600">Apply Glossary</span></label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="mtpe-pro-memory-toggle"><input type="checkbox" checked={checkMemory} onChange={(e) => setCheckMemory(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-purple-600" /><span className="text-sm text-gray-600">Check Memory</span></label>
            </div>
            <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700" data-testid="mtpe-pro-advanced-toggle"><Settings size={16} />Advanced{showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
          </div>
          {showAdvanced && (
            <div className="mt-4 pt-4 border-t border-gray-100" data-testid="mtpe-pro-advanced-panel">
              <h4 className="text-xs font-semibold text-gray-500 mb-3">PREBUILT INSTRUCTIONS</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                {Object.entries({ fixGrammar: 'Fix grammar & spelling', improveNaturalness: 'Improve naturalness', maintainMeaning: 'Maintain exact meaning', adaptIdioms: 'Adapt idioms', formalTone: 'Formal tone', keepTechnical: 'Keep technical terms' }).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50"><input type="checkbox" checked={instructions[key]} onChange={(e) => setInstructions(prev => ({ ...prev, [key]: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-purple-600" /><span className="text-sm text-gray-600">{label}</span></label>
                ))}
              </div>
              <div><label className="text-xs font-semibold text-gray-500 mb-1 block">CUSTOM INSTRUCTIONS</label><textarea value={customInstructions} onChange={(e) => setCustomInstructions(e.target.value)} placeholder="Add specific instructions..." rows={2} className="apple-input" data-testid="mtpe-pro-custom-instructions" /></div>
            </div>
          )}
        </CardApple.Body>
      </CardApple>

      {/* File Upload Area */}
      <div className={`border-2 border-dashed rounded-3xl p-12 text-center cursor-pointer transition-all ${isDragging ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} data-testid="file-dropzone">
        <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".docx,.xlsx,.txt,.pdf" multiple className="hidden" data-testid="file-input" />
        <Upload size={48} className={`mx-auto mb-4 ${isDragging ? 'text-purple-500' : 'text-gray-400'}`} />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{isDragging ? 'Drop files here' : 'Upload files for batch processing'}</h3>
        <p className="text-gray-500 mb-2">Drag & drop files here, or click to select</p>
        <p className="text-sm text-gray-400">Supported: DOCX, XLSX, TXT, PDF</p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <CardApple className="mt-6" data-testid="file-list">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Files in Batch ({files.length})</h3>
            <div className="flex gap-2">
              <ButtonApple onClick={processBatch} disabled={isProcessing || files.every(f => f.status === 'completed')} icon={isProcessing ? Loader2 : PlayCircle} data-testid="process-batch-btn">{isProcessing ? 'Processing...' : 'Process All'}</ButtonApple>
              {files.length > 1 && <ButtonApple variant="secondary" onClick={downloadAll} disabled={files.filter(f => f.status === 'completed').length === 0} icon={Package} data-testid="download-all-btn">Download All (ZIP)</ButtonApple>}
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {files.map(file => (
              <div key={file.id} className="flex items-center justify-between px-5 py-4" data-testid={`file-item-${file.id}`}>
                <div className="flex items-center gap-3">
                  {getFileIcon(file.file.name)}
                  <div>
                    <div className="font-medium text-gray-900">{file.file.name}</div>
                    <div className="text-sm text-gray-500">
                      {file.targetLang} • {(file.file.size / 1024).toFixed(1)} KB
                      {file.originalWordCount && <span className="ml-2">• {file.originalWordCount} words</span>}
                      {file.creditsUsed && <span className="ml-2 text-purple-600">• {file.creditsUsed.toFixed(2)} credits</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {file.status === 'pending' && <BadgeApple variant="gray" size="sm">Pending</BadgeApple>}
                  {file.status === 'processing' && <BadgeApple variant="blue" size="sm"><Loader2 size={12} className="animate-spin mr-1" />Processing</BadgeApple>}
                  {file.status === 'completed' && <BadgeApple variant="green" size="sm"><CheckCircle size={12} className="mr-1" />Completed</BadgeApple>}
                  {file.status === 'error' && <BadgeApple variant="red" size="sm" title={file.errorMessage}><AlertCircle size={12} className="mr-1" />Error</BadgeApple>}
                  
                  {/* Download button - always visible for completed files */}
                  {file.status === 'completed' && (
                    <ButtonApple 
                      variant="secondary" 
                      size="sm" 
                      onClick={() => downloadFile(file)} 
                      icon={Download}
                      data-testid={`download-file-${file.id}`}
                    >
                      Download
                    </ButtonApple>
                  )}
                  
                  <button onClick={() => removeFile(file.id)} disabled={file.status === 'processing'} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl disabled:opacity-50"><X size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </CardApple>
      )}

      {/* Cost Estimate */}
      {totalWordCount > 0 && (
        <div className="mt-6">
          <div style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'right', marginBottom: 4 }}>
            {files.some(f => f.wordCountSource === 'actual')
              ? `${totalWordCount} words (actual)`
              : files.some(f => f.wordCountSource === 'estimated')
              ? `~${totalWordCount} words (estimated)`
              : `${totalWordCount} words`}
          </div>
          <CostEstimateApple
            feature="mtpe_pro"
            targetWords={totalWordCount}
          />
        </div>
      )}
      {showExportFeedback && (
        <ExportFeedbackModal
          feature="mtpe_pro"
          onClose={() => setShowExportFeedback(false)}
        />
      )}
      <ReferralToast
        toast={referralToast}
        onDismiss={() => setReferralToast(null)}
      />
    </div>
  );
};

const QualityAnalysisPanel = () => {
  const [sourceFile, setSourceFile] = useState(null);
  const [mtFile, setMtFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [pdfPptNotice, setPdfPptNotice] = useState({ show: false, type: '', message: '' });

  const handleFileSelect = (file, type) => {
    if (!file) return;
    
    setMetrics(null);
    setAnalysisError(null);
    
    const ext = file.name.split('.').pop().toLowerCase();
    const supportedExts = ['doc', 'docx', 'txt', 'pdf', 'xlsx', 'xls', 'ppt', 'pptx'];
    
    if (!supportedExts.includes(ext)) {
      toast.error('Unsupported file format. Supported: DOC, DOCX, TXT, PDF, XLSX, PPT');
      return;
    }
    
    // Show notice for PDF and PPT files
    if (['pdf', 'ppt', 'pptx'].includes(ext)) {
      setPdfPptNotice({ 
        show: true, 
        type: ext === 'pdf' ? 'PDF' : 'PPT',
        message: ext === 'pdf' 
          ? 'PDF files may have formatting limitations. For best results, use DOCX or TXT.' 
          : 'PPT files will have slide text extracted. Complex layouts may not be preserved.'
      });
    }
    
    if (type === 'source') {
      setSourceFile(file);
    } else {
      setMtFile(file);
    }
  };

  const handleAnalysis = async () => {
    if (!sourceFile || !mtFile) { 
      toast.error('Please upload both source and MT output files'); 
      return; 
    }
    
    setIsAnalyzing(true);
    setAnalysisError(null);
    setMetrics(null);
    
    const token = localStorage.getItem('token');
    
    try {
      const formData = new FormData();
      formData.append('source_file', sourceFile);
      formData.append('mt_file', mtFile);
      
      const response = await fetch(`${API_URL}/api/mtpe/quality-analysis`, { 
        method: 'POST', 
        headers: { Authorization: `Bearer ${token}` }, 
        body: formData 
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || `Analysis failed (${response.status})`);
      }
      
      if (data.metrics) {
        setMetrics(data.metrics);
        toast.success('Quality analysis completed!');
      } else {
        throw new Error('No metrics returned from analysis');
      }
    } catch (error) {
      console.error('Quality analysis error:', error);
      setAnalysisError(error.message);
      toast.error(`Analysis failed: ${error.message}`);
    } finally { 
      setIsAnalyzing(false); 
    }
  };

  const getScoreClass = (score) => { if (score >= 90) return 'bg-green-100 text-green-800'; if (score >= 75) return 'bg-blue-100 text-blue-800'; if (score >= 60) return 'bg-amber-100 text-amber-800'; return 'bg-red-100 text-red-800'; };
  const getScoreGradient = (score) => { if (score >= 90) return 'from-green-500 to-emerald-500'; if (score >= 75) return 'from-blue-500 to-indigo-500'; if (score >= 60) return 'from-amber-500 to-orange-500'; return 'from-red-500 to-rose-500'; };

  return (
    <div data-testid="quality-analysis-panel">
      {/* PDF/PPT Notice */}
      {pdfPptNotice.show && (
        <CardApple className="mb-4 border-amber-200 bg-amber-50">
          <CardApple.Body>
            <div className="flex items-start gap-3">
              <Info size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">{pdfPptNotice.type} File Notice</p>
                <p className="text-sm text-amber-700">{pdfPptNotice.message}</p>
              </div>
              <button 
                onClick={() => setPdfPptNotice({ show: false, type: '', message: '' })}
                className="text-amber-600 hover:text-amber-800"
              >
                <X size={18} />
              </button>
            </div>
          </CardApple.Body>
        </CardApple>
      )}
      
      {/* File Upload */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <CardApple>
          <CardApple.Body>
            <h3 className="font-semibold text-gray-900 mb-3">Source Document</h3>
            <input type="file" accept=".doc,.docx,.txt,.pdf,.xlsx,.xls,.ppt,.pptx" onChange={(e) => handleFileSelect(e.target.files?.[0], 'source')} className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100" data-testid="source-file-input" />
            {sourceFile && <div className="mt-3 flex items-center gap-2 text-sm text-green-600"><CheckCircle size={14} />{sourceFile.name}</div>}
            <p className="mt-2 text-xs text-gray-400">Supported: DOC, DOCX, TXT, PDF, XLSX, PPT</p>
          </CardApple.Body>
        </CardApple>
        <CardApple>
          <CardApple.Body>
            <h3 className="font-semibold text-gray-900 mb-3">MTPE Output Document</h3>
            <input type="file" accept=".doc,.docx,.txt,.pdf,.xlsx,.xls,.ppt,.pptx" onChange={(e) => handleFileSelect(e.target.files?.[0], 'mt')} className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100" data-testid="mt-file-input" />
            {mtFile && <div className="mt-3 flex items-center gap-2 text-sm text-green-600"><CheckCircle size={14} />{mtFile.name}</div>}
            <p className="mt-2 text-xs text-gray-400">Supported: DOC, DOCX, TXT, PDF, XLSX, PPT</p>
          </CardApple.Body>
        </CardApple>
      </div>

      <ButtonApple onClick={handleAnalysis} disabled={isAnalyzing || !sourceFile || !mtFile} fullWidth icon={isAnalyzing ? Loader2 : BarChart3} className="mb-6" data-testid="run-analysis-btn">
        {isAnalyzing ? 'Analyzing...' : 'Run Quality Analysis'}
      </ButtonApple>

      {/* Error Display */}
      {analysisError && (
        <CardApple className="mb-6 border-red-200 bg-red-50">
          <CardApple.Body>
            <div className="flex items-center gap-3 text-red-700">
              <AlertCircle size={20} />
              <div>
                <p className="font-medium">Analysis Failed</p>
                <p className="text-sm">{analysisError}</p>
              </div>
            </div>
          </CardApple.Body>
        </CardApple>
      )}

      {/* Results */}
      {metrics && (
        <div className="space-y-6" data-testid="analysis-results">
          {/* Overall Score */}
          <CardApple>
            <CardApple.Body>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Overall Quality Score</h3>
                <span className={`text-3xl font-bold px-4 py-2 rounded-2xl ${getScoreClass(metrics.overallScore)}`}>{metrics.overallScore}/100</span>
              </div>
              <div className={`h-3 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}><div className={`h-full bg-gradient-to-r ${getScoreGradient(metrics.overallScore)} transition-all duration-500`} style={{ width: `${metrics.overallScore}%` }} /></div>
            </CardApple.Body>
          </CardApple>

          {/* Individual Metrics */}
          <div className="grid grid-cols-4 gap-4">
            {[{ label: 'Fluency', score: metrics.fluency }, { label: 'Accuracy', score: metrics.accuracy }, { label: 'Terminology', score: metrics.terminology }, { label: 'Grammar', score: metrics.grammar }].map(metric => (
              <CardApple key={metric.label}>
                <CardApple.Body>
                  <div className="text-sm text-gray-500 mb-1">{metric.label}</div>
                  <div className="text-2xl font-bold text-gray-900">{metric.score}/100</div>
                  <div className={`mt-2 h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}><div className={`h-full bg-gradient-to-r ${getScoreGradient(metric.score)}`} style={{ width: `${metric.score}%` }} /></div>
                </CardApple.Body>
              </CardApple>
            ))}
          </div>

          {/* Issues List */}
          {metrics.issues?.length > 0 && (
            <CardApple>
              <CardApple.Body>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Issues Detected ({metrics.issues.length})</h3>
                <div className="space-y-3">
                  {metrics.issues.map((issue, index) => (
                    <div key={index} className={`flex gap-3 p-4 rounded-2xl ${issue.type === 'error' ? 'bg-red-50 border border-red-200' : issue.type === 'warning' ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'}`}>
                      <div className="flex-shrink-0">
                        {issue.type === 'error' && <AlertCircle size={20} className="text-red-500" />}
                        {issue.type === 'warning' && <Info size={20} className="text-amber-500" />}
                        {issue.type === 'suggestion' && <CheckCircle size={20} className="text-blue-500" />}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{issue.category}</div>
                        <div className="text-sm text-gray-600">{issue.message}</div>
                        {issue.segment && <div className="text-xs text-gray-400 mt-1">Segment #{issue.segment}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardApple.Body>
            </CardApple>
          )}
        </div>
      )}
    </div>
  );
};

export default MTPEProPageApple;
